import { ExhibitionCollectionFragment } from '@/gql/generated/graphql'
import { AllPurposeLink } from '@components/all-purpose-link'
import { getPrefixedLinkPath } from '@components/prefixed-link'
import { HeadlineText, BodyText, CalendarPlusIcon } from 'design-configuration/components'
import { ExhibitionImageCurl } from '../../[slug]/components/exhibition-image-curl'
import { WorksDigitalReleaseCard } from '../../[slug]/components/works-digital-card'
import { WorksPhysicalReleaseCard } from '../../[slug]/components/works-physical-card'
import { iskraAnchor, iskraShopifyProduct, iskraTitle } from '../display'
import { WorksQuote } from '../../[slug]/components/works'
import { articleRoute } from '@utils/routes'
import { AddToCalendarButton, getProjectEvent, TrackedAddToCalButtonNames } from '@components/add-to-calendar'
import { ValidStatusType } from '@utils/project'
import { useProjectStatus } from '@/hooks/use-project-status'
import { GROUP_EXHIBITION_Q4_2024_SLUG } from '@consts/common'
import { getPhysicalProjectEvent } from '../../[slug]/utils/exhibition-utils'

export const IskraWork = ({ collection }: { collection?: ExhibitionCollectionFragment }): React.ReactElement | null => {
  const digitalProject = collection?.Collection?.Project ?? undefined
  const physicalProject = collection?.AdditionalProjects?.[0]?.Project ?? undefined

  const { status: digitalProjectStatus } = useProjectStatus(digitalProject)
  const { status: physicalProjectStatus } = useProjectStatus(physicalProject)

  if (!digitalProject || !physicalProject) {
    return null
  }

  const digitalEvent = getProjectEvent(digitalProject)
  const physicalEvent = getPhysicalProjectEvent(physicalProject, GROUP_EXHIBITION_Q4_2024_SLUG)

  return (
    <div className="gap-y-2xl flex flex-col md:gap-y-[80px]">
      <ExhibitionImageCurl
        anchor={iskraAnchor}
        image1AspectRatio="aspect-[4/5]"
        align={'right'}
        image1={getPrefixedLinkPath('/images/poetics/iskraCollection/iskraCollection-1.jpg')}
        image2={getPrefixedLinkPath('/images/poetics/iskraCollection/iskraCollection-2.jpg')}
        image3={getPrefixedLinkPath('/images/poetics/iskraCollection/iskraCollection-3.jpg')}
        content={
          <>
            <div className="flex items-center justify-between">
              <div>
                <HeadlineText className="font-medium">{iskraTitle}</HeadlineText>
                <HeadlineText className="font-light">Iskra Velitchkova</HeadlineText>
              </div>
              {digitalProjectStatus === ValidStatusType.upcoming ||
              physicalProjectStatus === ValidStatusType.upcoming ? (
                <AddToCalendarButton
                  events={[physicalEvent, digitalEvent]}
                  buttonName={TrackedAddToCalButtonNames.exhibitionWorksDescription}
                  colorTheme={'outline'}
                  className="bg-transparent"
                >
                  <div className="gap-x-xs flex items-center">
                    <CalendarPlusIcon className="fill-on-surface h-sm w-sm" />
                    <BodyText>Add to calendar</BodyText>
                  </div>
                </AddToCalendarButton>
              ) : null}
            </div>
            <BodyText>
              Iskra Velitchkova’s <span className="italic">INK</span> invokes infinity through the interplay of
              ephemeral fragrances and delicate, recursive lines. In her collection{' '}
              <span className="italic">INK (fragrances)</span>, the fragrance becomes a vessel for memory, inviting
              contemplation of the elusive nature of human experiences. Each generative fragrance, crafted through an
              algorithmic process that ensures no two are alike, transcends mere visual representation, offering a
              visceral encounter that immerses both mind and body. The digital-only release of{' '}
              <span className="italic">INK</span> expands on these themes, delving into memory through recursive
              drawing, striving to articulate the space where the past can be both recaptured and transcended.
            </BodyText>
          </>
        }
      />
      <div className="gap-md px-md lg:gap-2xl lg:px-2xl flex w-full flex-col justify-center md:flex-row">
        <WorksPhysicalReleaseCard
          project={physicalProject}
          exhibitionSlug={GROUP_EXHIBITION_Q4_2024_SLUG}
          title={'INK (fragrances)'}
          image={getPrefixedLinkPath('/images/poetics/iskraCollection/iskraCollection-4.jpg')}
          moreDetailsBlocks={[
            {
              title: 'Edition details',
              description: (
                <BodyText>
                  Each <span className="italic">INK</span> fragrance is produced at the time of purchase through an
                  algorithmic process developed in tandem with perfumer Holladay Saltz, formerly of Apoteker Tepe and Le
                  Labo Fragrances. Each formula is wholly unique, yet encapsulates the ethos of{' '}
                  <span className="italic">INK</span>: evocative, linear, ambiguous, and haunting.
                </BodyText>
              )
            },
            {
              title: 'Product specifications',
              description: (
                <div className="gap-y-xs flex flex-col">
                  <BodyText>
                    Each 50ml generative fragrance is contained in a glass atomizer bottle and also includes a
                    hand-blown glass sculptural vessel. The fragrance may be decanted into the vessel if desired.
                  </BodyText>
                  <BodyText>
                    <span className="italic">INK</span> fragrance formulas are unisex and wholly unique, with herbal,
                    woody, spicy profiles. Each may feature notes of iron gall ink, India ink, sweet vernal grass, pink
                    and black pepper, fir balsam, hinoki cedar, Bulgarian rose absolute, ambrette seed, Buddhawood,
                    rockrose, guaiacwood, and Peru balsam.
                  </BodyText>
                  <BodyText>
                    All fragrances are suitable for wearing but may cause irritation for those with skin sensitivities
                    or allergies. Please discontinue use if irritation occurs.
                  </BodyText>
                </div>
              )
            },
            {
              title: 'Sales Notes',
              description: (
                <BodyText>
                  Available for purchase with credit card, Shop Pay, Apple Pay, Google Pay, PayPal, or Ethereum. Each
                  purchase includes a generative fragrance, sculptural vessel, and corresponding digital artwork, which
                  can be claimed upon purchase. All fragrances are made to order.
                </BodyText>
              )
            },
            {
              title: 'Shipping and returns',
              description: (
                <BodyText>
                  Fragrances will be shipped in the order of purchase. Please allow 2-4 weeks for shipping after
                  purchase. Shipping is only available in North America (U.S., Canada, & Mexico) and the European Union
                  and the U.K. Shipping and handling is complimentary with all orders, however duties and taxes may
                  still apply at your destination. No returns.
                </BodyText>
              )
            }
          ]}
          description={
            <div className="my-sm">
              <BodyText className="font-medium">
                Edition of 6 unique fragrances and vessels with digital artworks
              </BodyText>
              <BodyText>Fixed price of $1,000 USD</BodyText>
            </div>
          }
          shopifyProduct={iskraShopifyProduct}
        />
        <WorksDigitalReleaseCard
          project={digitalProject}
          title={'INK'}
          moreDetailsBlocks={[
            {
              title: 'Release details',
              description: (
                <div className="gap-y-xs flex flex-col">
                  <BodyText>
                    In a ranked auction with settlement, bids are placed by collectors and ranked from highest to
                    lowest. Any outbid funds are returned immediately. At the conclusion of the auction, winners pay the
                    lowest winning bid price, and any excess funds are returned upon minting the artwork.
                  </BodyText>
                  <BodyText className="text-on-surface-variant">
                    Time extensions apply: the auction will extend by up to 5 minutes if a bid is placed in the final 5
                    minutes, with a maximum total extension of 60 minutes.
                  </BodyText>
                  <AllPurposeLink
                    href={articleRoute('art-blocks-how-to-purchase')}
                    className="text-on-surface-variant gap-x-2xs flex items-center underline"
                  >
                    <BodyText>Learn more</BodyText>
                  </AllPurposeLink>
                </div>
              )
            },
            {
              title: 'Sales Notes',
              description: (
                <BodyText>
                  All bids must be placed in ETH. Winners will receive their digital artwork directly in their wallets
                  after the auction.
                </BodyText>
              )
            }
          ]}
          description={
            <div className="my-sm">
              <BodyText className="font-medium">100 unique digital artworks</BodyText>
              <BodyText>Ranked auction with settlement with a starting bid of 0.05 Ξ</BodyText>
            </div>
          }
        />
      </div>
      <WorksQuote
        quote={
          <>
            “<span className="italic">INK</span> speaks to the pursuit of permanence in a world destined to vanish.
            Whether in blue or black, in pencil or rust, each line reflects fragments, yet none of it is real; it is
            only experiences, biases, dreams, and demons. To this, <span className="italic">INK</span> adds fragrance, a
            scent that declares “I was here, I existed.” For a moment, breathe it in, take in the work. In this quiet
            persistence, we find redemption—an echo of those who remain and those who have already gone.”
          </>
        }
        author="Iskra Velitchkova"
        image={getPrefixedLinkPath('/images/poetics/iskraCollection/iskraCollection-5.jpg')}
        alignText="left"
      />
    </div>
  )
}
